import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { KeyRound, PlusCircle, Pencil, Trash2, Loader2, Copy } from "lucide-react";

const EMPTY = { name: "", email: "", password: "", richiedente_id: "", active: true };

const AccessoDialog = ({ open, editing, richiedenti, onClose, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(editing ? { ...EMPTY, ...editing, password: "" } : EMPTY); }, [open, editing]);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.richiedente_id) return toast.error("Seleziona il Richiedente collegato");
    setSaving(true);
    try {
      if (editing) await api.put(`/strutture/accessi/${editing.id}`, f); else await api.post("/strutture/accessi", f);
      toast.success(editing ? "Accesso aggiornato" : "Accesso creato", { description: f.password ? "Comunica le credenziali alla struttura." : undefined });
      onSaved();
    } catch (err) { toast.error("Non salvato", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">{editing ? "Modifica accesso struttura" : "Nuovo accesso struttura"}</DialogTitle>
          <DialogDescription>La struttura entra da /strutture/login e vede solo le proprie richieste.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-1">
          <div>
            <Label className="nx-label">Richiedente in anagrafica *</Label>
            <Select value={f.richiedente_id} onValueChange={(v) => setF({ ...f, richiedente_id: v })}>
              <SelectTrigger data-testid="acc-richiedente" className="mt-1.5"><SelectValue placeholder="Seleziona richiedente" /></SelectTrigger>
              <SelectContent>{richiedenti.map((r) => <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-[11px] text-slate-400 mt-1">Non c'è? Crealo prima in Anagrafica Richiedenti.</p>
          </div>
          <div><Label className="nx-label">Nome utente / referente</Label><Input data-testid="acc-name" className="mt-1.5" placeholder="Es. Reparto Cardiologia" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><Label className="nx-label">Email *</Label><Input data-testid="acc-email" type="email" required className="mt-1.5" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div>
            <Label className="nx-label">{editing ? "Nuova password (lascia vuoto per non cambiarla)" : "Password * (min. 8 caratteri)"}</Label>
            <Input data-testid="acc-password" type="text" required={!editing} minLength={8} className="mt-1.5" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
          </div>
          <label className="flex items-center gap-3"><Switch data-testid="acc-active" checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /><span className="text-sm text-slate-700">{f.active ? "Accesso attivo" : "Accesso disattivato"}</span></label>
          <DialogFooter><Button data-testid="acc-save" type="submit" disabled={saving} className="w-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function StruttureAccessiPage() {
  const [items, setItems] = useState(null);
  const [richiedenti, setRichiedenti] = useState([]);
  const [dlg, setDlg] = useState({ open: false, editing: null });
  const load = useCallback(async () => {
    const [a, r] = await Promise.all([api.get("/strutture/accessi"), api.get("/richiedenti")]);
    setItems(a.data); setRichiedenti(r.data);
  }, []);
  useEffect(() => { load().catch(() => setItems([])); }, [load]);
  const portalUrl = `${window.location.origin}/strutture/login`;

  const remove = async (a) => {
    if (!window.confirm(`Eliminare l'accesso di ${a.email}? Le richieste già inviate restano registrate.`)) return;
    try { await api.delete(`/strutture/accessi/${a.id}`); toast.success("Accesso eliminato"); load(); } catch { toast.error("Non eliminato"); }
  };

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto nx-fade-in pb-10">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2"><KeyRound className="h-7 w-7 text-primary" /> Accessi Strutture</h1>
            <p className="text-sm text-slate-500 mt-1 flex flex-wrap items-center gap-1.5">Link portale: <span data-testid="acc-portal-url" className="font-mono text-xs text-slate-700">{portalUrl}</span>
              <button type="button" data-testid="acc-copy-url" className="text-primary hover:text-nexus-gold" title="Copia link" onClick={() => { navigator.clipboard?.writeText(portalUrl); toast.success("Link copiato"); }}><Copy className="h-3.5 w-3.5" /></button>
            </p>
          </div>
          <Button data-testid="acc-new" className="gap-2" onClick={() => setDlg({ open: true, editing: null })}><PlusCircle className="h-4 w-4" /> Nuovo accesso</Button>
        </div>
        <Card className="border-slate-200 overflow-hidden">
          {items === null ? <div className="p-5"><Skeleton className="h-24 w-full" /></div> : items.length === 0 ? (
            <p className="p-10 text-center text-sm text-slate-500">Nessun accesso creato.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Struttura (Richiedente)</TableHead><TableHead>Utente</TableHead><TableHead>Email</TableHead><TableHead>Stato</TableHead><TableHead /></TableRow></TableHeader>
                <TableBody>
                  {items.map((a) => (
                    <TableRow key={a.id} data-testid={`acc-row-${a.id}`}>
                      <TableCell className="text-sm font-medium text-slate-800">{a.richiedente_nome}</TableCell>
                      <TableCell className="text-sm text-slate-600">{a.name}</TableCell>
                      <TableCell className="text-sm text-slate-600">{a.email}</TableCell>
                      <TableCell><span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${a.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{a.active ? "Attivo" : "Disattivato"}</span></TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button data-testid={`acc-edit-${a.id}`} size="sm" variant="ghost" className="gap-1.5" onClick={() => setDlg({ open: true, editing: a })}><Pencil className="h-4 w-4" /> Modifica</Button>
                        <Button data-testid={`acc-delete-${a.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => remove(a)}><Trash2 className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>
      <AccessoDialog {...dlg} richiedenti={richiedenti} onClose={() => setDlg({ open: false, editing: null })} onSaved={() => { setDlg({ open: false, editing: null }); load(); }} />
    </AppLayout>
  );
}
